import React from "react";

export default function CubicSignShape({
  risingRight = true,
  leftLabel = "-2",
  middleLabel = "0",
  rightLabel = "2",
  signs = ["-", "+", "-", "+"],
  width = 560,
  height = 230,
  axisColor = "currentColor",
  curveColor = "#f59e0b",
}) {
  const leftPad = 36;
  const rightPad = 36;
  const topPad = 28;
  const bottomPad = 42;

  const xAxisY = height * 0.56;
  const usableW = width - leftPad - rightPad;
  const x1 = leftPad + usableW * 0.28;
  const x2 = leftPad + usableW * 0.5;
  const x3 = leftPad + usableW * 0.72;

  const yHigh = topPad + 26;
  const yLow = height - bottomPad - 26;

  const startX = leftPad + 8;
  const endX = width - rightPad - 8;

  // Build one stable "cubic-sign sketch" template and mirror it vertically
  // (around OX) for the opposite sign configuration.
  const orientation = signs[0] === "+" ? 1 : -1;
  const yAt = (delta) => xAxisY - orientation * delta;
  const firstMidX = (x1 + x2) / 2;
  const secondMidX = (x2 + x3) / 2;

  const p0 = [startX, yAt(62)];
  const p1 = [x1, xAxisY];
  const p2 = [x2, xAxisY];
  const p3 = [x3, xAxisY];
  const p4 = [endX, yAt(-62)];

  // Stable sketch template:
  // - outer parts: almost straight
  // - middle parts: two smooth "parabola-like" arcs
  // Variant 2 is mirror over OX via `orientation`.
  const lowArcY = yAt(-84);
  const highArcY = yAt(84);

  const path = `M ${p0[0]} ${p0[1]}
                Q ${x1 - 42} ${yAt(28)}, ${x1 - 20} ${yAt(10)}
                Q ${x1 - 8} ${yAt(2)}, ${p1[0]} ${p1[1]}
                Q ${firstMidX} ${lowArcY}, ${p2[0]} ${p2[1]}
                Q ${secondMidX} ${highArcY}, ${p3[0]} ${p3[1]}
                Q ${x3 + 8} ${yAt(-2)}, ${x3 + 20} ${yAt(-10)}
                Q ${x3 + 42} ${yAt(-28)}, ${p4[0]} ${p4[1]}`;

  const radius = 14;
  const signFont = 24;
  const signYOffset = 22;
  const leftTopY = xAxisY - signYOffset;
  const leftBottomY = xAxisY + signYOffset;

  const signPositions = [
    { x: leftPad + usableW * 0.12, y: signs[0] === "+" ? leftTopY : leftBottomY },
    { x: leftPad + usableW * 0.39, y: signs[1] === "+" ? leftTopY : leftBottomY },
    { x: leftPad + usableW * 0.61, y: signs[2] === "+" ? leftTopY : leftBottomY },
    { x: leftPad + usableW * 0.88, y: signs[3] === "+" ? leftTopY : leftBottomY },
  ];

  const SignBubble = ({ x, y, sign }) => (
    <g>
      <circle cx={x} cy={y} r={radius} fill="none" stroke={curveColor} strokeWidth="2.2" />
      {sign === "+" ? (
        <>
          <line x1={x - 6} y1={y} x2={x + 6} y2={y} stroke={curveColor} strokeWidth="2.4" strokeLinecap="round" />
          <line x1={x} y1={y - 6} x2={x} y2={y + 6} stroke={curveColor} strokeWidth="2.4" strokeLinecap="round" />
        </>
      ) : (
        <line x1={x - 7} y1={y} x2={x + 7} y2={y} stroke={curveColor} strokeWidth="2.6" strokeLinecap="round" />
      )}
    </g>
  );

  const Tick = ({ x, label }) => (
    <g>
      <line x1={x} y1={xAxisY - 6} x2={x} y2={xAxisY + 6} stroke={axisColor} strokeWidth="1.8" />
      <text
        x={x}
        y={xAxisY + 28}
        textAnchor="middle"
        dominantBaseline="hanging"
        fontSize="17"
        fontFamily="KaTeX_Main, STIXGeneral, 'Times New Roman', serif"
        fill={axisColor}
      >
        {label}
      </text>
    </g>
  );

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img">
      <line x1={leftPad} y1={xAxisY} x2={width - rightPad} y2={xAxisY} stroke={axisColor} strokeWidth="1.9" />
      <path
        d={`M ${width - rightPad - 10} ${xAxisY - 5} L ${width - rightPad} ${xAxisY} L ${width - rightPad - 10} ${xAxisY + 5} Z`}
        fill={axisColor}
      />

      <path d={path} fill="none" stroke={curveColor} strokeWidth="4" strokeLinecap="round" />

      <Tick x={x1} label={leftLabel} />
      <Tick x={x2} label={middleLabel} />
      <Tick x={x3} label={rightLabel} />

      {signs.map((sign, idx) => (
        <SignBubble key={`${sign}-${idx}`} x={signPositions[idx].x} y={signPositions[idx].y} sign={sign} />
      ))}
    </svg>
  );
}
